import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI, Conversation, Message } from '../api';

const PAGE_SIZE = 30;

const Messages: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesScrollRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const conversationsLengthRef = useRef(0);
  /** Stops the 2s poll from re-adding a thread until DELETE finishes on the server */
  const pendingDeletedOtherIds = useRef<Set<number>>(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [showReportModal, setShowReportModal] = useState(false);
  const [showConversationMenu, setShowConversationMenu] = useState(false);
  const [reportReason, setReportReason] = useState('harassment');
  const [reportDetails, setReportDetails] = useState('');
  const [moderationBusy, setModerationBusy] = useState(false);
  const conversationMenuRef = useRef<HTMLDivElement>(null);

  const reportReasonOptions = [
    { value: 'harassment', label: 'Harassment' },
    { value: 'spam', label: 'Spam' },
    { value: 'scam', label: 'Scam or fraud' },
    { value: 'inappropriate', label: 'Inappropriate content' },
    { value: 'other', label: 'Other' },
  ];

  const allowsChatModeration =
    (selectedConversation?.other_user_name?.trim().toLowerCase() ?? '') !== 'buydrop';

  useEffect(() => {
    setShowConversationMenu(false);
  }, [selectedConversation?.other_user_id]);

  useEffect(() => {
    if (!showConversationMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      if (conversationMenuRef.current && !conversationMenuRef.current.contains(e.target as Node)) {
        setShowConversationMenu(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowConversationMenu(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showConversationMenu]);

  const openReportFromMenu = () => {
    setShowConversationMenu(false);
    setShowReportModal(true);
  };

  const handleBlockUser = async () => {
    if (!selectedConversation?.other_user_id) return;
    setShowConversationMenu(false);
    if (!window.confirm('Block this user? You will not be able to chat with them.')) return;
    setModerationBusy(true);
    try {
      await messagesAPI.blockUser(selectedConversation.other_user_id);
      toast.success('User blocked');
      setSelectedConversation(null);
      setMessages([]);
      await loadConversations();
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Could not block user');
    } finally {
      setModerationBusy(false);
    }
  };

  const handleSubmitReport = async () => {
    if (!selectedConversation?.other_user_id) return;
    setModerationBusy(true);
    try {
      await messagesAPI.reportUser({
        reported_user_id: selectedConversation.other_user_id,
        reason: reportReason,
        details: reportDetails.trim() || undefined,
        listing_id: selectedConversation.listing_id,
      });
      toast.success('Report submitted. Our team will review it.');
      setShowReportModal(false);
      setReportDetails('');
    } catch (err: any) {
      toast.error(err.response?.data?.error || err.message || 'Could not submit report');
    } finally {
      setModerationBusy(false);
    }
  };

  useEffect(() => { conversationsLengthRef.current = conversations.length; }, [conversations.length]);

  useEffect(() => {
    if (!isAuthenticated) return;
    
    loadConversations();
    loadUnreadCount();
  }, [isAuthenticated]);

  // Handle navigation state to start conversation
  useEffect(() => {
    if (!isAuthenticated || !location.state?.sellerId && !location.state?.buyerId) return;
    
    const otherUserId = location.state.sellerId || location.state.buyerId;
    const listingId = location.state.listingId;
    
    // Wait for conversations to load, then find or create conversation
    if (conversations.length > 0 || !loading) {
      const existingConv = conversations.find(c => c.other_user_id === otherUserId);
      if (existingConv) {
        setSelectedConversation(existingConv);
      } else {
        // Create a temporary conversation object
        setSelectedConversation({
          other_user_id: otherUserId,
          other_user_name: location.state.buyerName || location.state.sellerName || 'User',
          listing_id: listingId,
          listing_title: location.state.listingTitle,
          listing_image: location.state.listingImage,
          unread_count: 0,
        });
      }
    }
  }, [isAuthenticated, location.state, conversations, loading]);

  // Load messages when conversation is selected
  useEffect(() => {
    if (!isAuthenticated || !selectedConversation) return;
    shouldStickToBottomRef.current = true;
    loadMessages({ forceScroll: true });
  }, [selectedConversation, isAuthenticated]);

  // Polling interval for real-time updates
  useEffect(() => {
    if (!isAuthenticated) return;
    
    const interval = setInterval(() => {
      const limit = Math.max(PAGE_SIZE, conversationsLengthRef.current);
      loadConversations(false, limit);
      loadUnreadCount();
      if (selectedConversation) {
        loadMessages();
      }
    }, 2000); // Poll every 2 seconds for better responsiveness
    
    return () => clearInterval(interval);
  }, [isAuthenticated, selectedConversation]);

  const loadConversations = async (append = false, pollLimit?: number) => {
    if (!isAuthenticated) return;
    if (append) setLoadingMore(true);
    try {
      const fetchOffset = append ? offset : 0;
      const limit = pollLimit ?? PAGE_SIZE;
      const data = await messagesAPI.getConversations(append ? { limit: PAGE_SIZE, offset: fetchOffset } : { limit, offset: 0 });
      let arr = Array.isArray(data) ? data : [];
      const pending = pendingDeletedOtherIds.current;
      arr = arr.filter((c) => !pending.has(Number(c.other_user_id)));
      if (append) {
        setConversations(prev => [...prev, ...arr]);
        setOffset(fetchOffset + arr.length);
        setHasMore(arr.length >= PAGE_SIZE);
      } else {
        setConversations(arr);
        setOffset(arr.length);
        setHasMore(arr.length >= limit);
      }
      setError('');
    } catch (err: any) {
      console.error('Error loading conversations:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to load conversations';
      
      // Don't show error for 401/403 - user will be redirected
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        setError(errorMsg);
      }
      
      // Token expiration is handled by API interceptor, but we can still navigate
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Small delay to let the interceptor clear the token first
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 100);
      }
    } finally {
      setLoading(false);
      if (append) setLoadingMore(false);
    }
  };

  const isNearBottom = (el: HTMLDivElement, thresholdPx = 80) =>
    el.scrollHeight - el.scrollTop - el.clientHeight <= thresholdPx;

  const handleMessagesScroll = () => {
    const el = messagesScrollRef.current;
    if (!el) return;
    shouldStickToBottomRef.current = isNearBottom(el);
  };

  const loadMessages = async (options?: { forceScroll?: boolean }) => {
    if (!selectedConversation || !isAuthenticated) return;
    if (options?.forceScroll) {
      shouldStickToBottomRef.current = true;
    }
    try {
      const data = await messagesAPI.getMessages(
        selectedConversation.other_user_id,
        selectedConversation.listing_id
      );
      setMessages((prev) => {
        if (
          prev.length === data.length &&
          prev.length > 0 &&
          prev[prev.length - 1]?.id === data[data.length - 1]?.id
        ) {
          return prev;
        }
        return data;
      });
    } catch (err: any) {
      console.error('Error loading messages:', err);
      // Token expiration is handled by API interceptor
      if (err.response?.status === 401 || err.response?.status === 403) {
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 100);
      }
    }
  };

  const loadUnreadCount = async () => {
    if (!isAuthenticated) return;
    try {
      const data = await messagesAPI.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch (err: any) {
      console.error('Error loading unread count:', err);
      // Token expiration is handled by API interceptor
      if (err.response?.status === 401 || err.response?.status === 403) {
        setTimeout(() => {
          navigate('/login', { replace: true });
        }, 100);
      }
    }
  };

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    setMessages([]);
  };

  const handleDeleteConversation = async (e: React.MouseEvent, conv: Conversation) => {
    e.stopPropagation();
    const oid = Number(conv.other_user_id);
    if (!oid || deletingId != null) return;
    if (!window.confirm('Delete this conversation? This removes all messages for both you and the other person.')) return;
    pendingDeletedOtherIds.current.add(oid);
    setDeletingId(oid);
    setConversations((prev) => prev.filter((c) => Number(c.other_user_id) !== oid));
    if (selectedConversation && Number(selectedConversation.other_user_id) === oid) {
      setSelectedConversation(null);
      setMessages([]);
    }
    try {
      await messagesAPI.deleteConversation(oid);
      toast.success('Conversation deleted');
      await loadUnreadCount();
    } catch (err: any) {
      pendingDeletedOtherIds.current.delete(oid);
      const msg = err.response?.data?.error || err.message || 'Could not delete conversation';
      toast.error(msg);
      await loadConversations(false, Math.max(PAGE_SIZE, conversationsLengthRef.current));
    } finally {
      pendingDeletedOtherIds.current.delete(oid);
      setDeletingId(null);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedConversation || sending) return;

    if (!selectedConversation.other_user_id) {
      toast.error('Invalid conversation. Please select a conversation.');
      return;
    }

    setSending(true);
    setError('');
    try {
      await messagesAPI.send(
        selectedConversation.other_user_id,
        newMessage.trim(),
        selectedConversation.listing_id
      );
      setNewMessage('');
      shouldStickToBottomRef.current = true;
      await loadMessages({ forceScroll: true });
      await loadConversations();
      await loadUnreadCount();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || 'Failed to send message';
      console.error('Error sending message:', err);
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const scrollToBottom = (behavior: ScrollBehavior = 'smooth') => {
    const el = messagesScrollRef.current;
    if (el) {
      el.scrollTo({ top: el.scrollHeight, behavior });
      return;
    }
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  useEffect(() => {
    if (!shouldStickToBottomRef.current || messages.length === 0) return;
    requestAnimationFrame(() => scrollToBottom('auto'));
  }, [messages]);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Please log in to view messages</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition"
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-200 border-t-green-500"></div>
          <p className="mt-4 text-gray-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-4 sm:py-6">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">Messages</h1>

        {error && (
          <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" style={{ height: 'calc(100vh - 180px)', minHeight: '500px' }}>
          <div className="flex h-full flex-col sm:flex-row">
            {/* Conversations List */}
            <div className="w-full sm:w-1/3 border-b sm:border-b-0 sm:border-r border-gray-200 overflow-y-auto flex-shrink-0" style={{ maxHeight: '300px', height: 'auto' }}>
              {conversations.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <p>No conversations yet</p>
                  <p className="text-sm mt-2">Start messaging sellers from their listings!</p>
                </div>
              ) : (
                <div>
                  {conversations.map((conv) => (
                    <div
                      key={conv.other_user_id}
                      className={`flex items-stretch border-b border-gray-200 ${
                        selectedConversation?.other_user_id === conv.other_user_id
                          ? 'bg-green-50 border-l-4 border-l-green-500'
                          : ''
                      }`}
                    >
                    <button
                      type="button"
                      onClick={() => handleSelectConversation(conv)}
                      className="flex-1 min-w-0 p-4 text-left hover:bg-gray-50 transition"
                    >
                      <div className="flex items-start space-x-3">
                        {conv.listing_image ? (
                          <img
                            src={conv.listing_image}
                            alt={conv.listing_title}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
                            {conv.other_user_name.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1 gap-2">
                            <p className="font-semibold text-gray-900 truncate min-w-0 overflow-hidden">
                              {conv.other_user_name}
                            </p>
                            {conv.unread_count > 0 && (
                              <span className="bg-green-500 text-white text-xs font-bold rounded-full px-2 py-0.5">
                                {conv.unread_count}
                              </span>
                            )}
                          </div>
                          {conv.listing_title && (
                            <p className="text-xs text-gray-500 truncate mb-1 overflow-hidden">
                              Re: {conv.listing_title}
                            </p>
                          )}
                          {conv.last_message && (
                            <p className="text-sm text-gray-600 truncate overflow-hidden">
                              {conv.last_message}
                            </p>
                          )}
                          {conv.last_message_time && (
                            <p className="text-xs text-gray-400 mt-1">
                              {formatTime(conv.last_message_time)}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                    <button
                      type="button"
                      title="Delete conversation"
                      aria-label="Delete conversation"
                      disabled={deletingId === Number(conv.other_user_id)}
                      onClick={(e) => handleDeleteConversation(e, conv)}
                      className="shrink-0 px-3 text-gray-400 hover:text-red-600 hover:bg-red-50 transition disabled:opacity-50"
                    >
                      {deletingId === Number(conv.other_user_id) ? (
                        <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
                      ) : (
                        <span className="text-lg leading-none" aria-hidden>🗑</span>
                      )}
                    </button>
                    </div>
                  ))}
                  {hasMore && (
                    <div className="p-3 border-t border-gray-200">
                      <button
                        onClick={() => loadConversations(true)}
                        disabled={loadingMore}
                        className="w-full py-2 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg transition disabled:opacity-70 flex items-center justify-center gap-2"
                      >
                        {loadingMore ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-green-500 border-t-transparent" />
                            Loading...
                          </>
                        ) : (
                          'Load more conversations'
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Messages View */}
            <div className="flex-1 flex flex-col min-h-0" style={{ minHeight: '400px' }}>
              {selectedConversation ? (
                <>
                  {/* Header */}
                  <div className="p-4 border-b border-gray-200 bg-white">
                    <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center space-x-3 min-w-0">
                      {selectedConversation.listing_image ? (
                        <img
                          src={selectedConversation.listing_image}
                          alt={selectedConversation.listing_title}
                          className="w-10 h-10 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
                          {selectedConversation.other_user_name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {selectedConversation.other_user_name}
                        </h3>
                        {selectedConversation.listing_title && (
                          <p className="text-xs text-gray-500 truncate">
                            Re: {selectedConversation.listing_title}
                          </p>
                        )}
                      </div>
                    </div>
                    {selectedConversation.other_user_id && allowsChatModeration ? (
                      <div className="relative flex-shrink-0" ref={conversationMenuRef}>
                        <button
                          type="button"
                          onClick={() => setShowConversationMenu((open) => !open)}
                          disabled={moderationBusy}
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 hover:text-gray-900 disabled:opacity-50"
                          aria-label="Conversation options"
                          aria-expanded={showConversationMenu}
                          aria-haspopup="menu"
                        >
                          <i className="ri-more-2-fill text-xl" aria-hidden />
                        </button>
                        {showConversationMenu && (
                          <div
                            role="menu"
                            className="absolute right-0 top-full z-20 mt-1 min-w-[10rem] rounded-lg border border-gray-200 bg-white py-1 shadow-sm"
                          >
                            <button
                              type="button"
                              role="menuitem"
                              onClick={openReportFromMenu}
                              disabled={moderationBusy}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              <i className="ri-flag-line text-base text-gray-500" aria-hidden />
                              Report
                            </button>
                            <button
                              type="button"
                              role="menuitem"
                              onClick={handleBlockUser}
                              disabled={moderationBusy}
                              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              <i className="ri-forbid-line text-base" aria-hidden />
                              Block
                            </button>
                          </div>
                        )}
                      </div>
                    ) : null}
                    </div>
                  </div>

                  {showReportModal && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                      <div className="bg-white rounded-xl shadow-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Report user</h3>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                        <select
                          value={reportReason}
                          onChange={(e) => setReportReason(e.target.value)}
                          className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                          {reportReasonOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Details (optional)</label>
                        <textarea
                          value={reportDetails}
                          onChange={(e) => setReportDetails(e.target.value)}
                          rows={3}
                          className="w-full mb-4 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          placeholder="What happened?"
                        />
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => setShowReportModal(false)}
                            className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg"
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleSubmitReport}
                            disabled={moderationBusy}
                            className="px-4 py-2 text-sm bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                          >
                            Submit report
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Messages */}
                  <div
                    ref={messagesScrollRef}
                    onScroll={handleMessagesScroll}
                    className="flex-1 overflow-y-auto p-4 bg-gray-50"
                  >
                    {messages.length === 0 ? (
                      <div className="text-center text-gray-500 py-8">
                        <p>No messages yet. Start the conversation!</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {messages.map((msg) => {
                          const isSender = msg.sender_id === user?.id;
                          return (
                            <div
                              key={msg.id}
                              className={`flex ${isSender ? 'justify-end' : 'justify-start'}`}
                            >
                              <div
                                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                  isSender
                                    ? 'bg-green-500 text-white'
                                    : 'bg-white text-gray-900 border border-gray-200'
                                }`}
                              >
                                <p className="text-sm">{msg.message}</p>
                                <p
                                  className={`text-xs mt-1 ${
                                    isSender ? 'text-green-100' : 'text-gray-400'
                                  }`}
                                >
                                  {formatTime(msg.created_at)}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </div>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className="p-4 border-t border-gray-200 bg-white">
                    {error && (
                      <div className="mb-2 p-2 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                        {error}
                      </div>
                    )}
                    <form onSubmit={handleSendMessage} className="flex space-x-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => {
                          setNewMessage(e.target.value);
                          setError(''); // Clear error when user types
                        }}
                        placeholder="Type a message..."
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        disabled={sending}
                      />
                      <button
                        type="submit"
                        disabled={!newMessage.trim() || sending || !selectedConversation?.other_user_id}
                        className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {sending ? 'Sending...' : 'Send'}
                      </button>
                    </form>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <p className="text-xl mb-2">💬</p>
                    <p>Select a conversation to start messaging</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Messages;

